import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { FLEX_CONTAINER, FLEX_CONTAINER_CENTERED, FLEX_ITEM_AUTO, FLEX_WRAP, FONT_WEIGHT, MARGIN } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { KeyboardInput, createHtmlElement } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { PropertyConfig } from "@cervval.js/common/src/forms/propertyConfig";
import { PropertyConfigType } from "@cervval.js/common/src/forms/propertyConfig.types";
import { validatorEntry } from "@cervval.js/common/src/services/validation.impl/validators/validator";
import { anyReallyAny, clone } from "@cervval.js/common/src/tools/object.tools";
import { DEFAULT_DATE_FORMAT, beginOfNext, extractLooseParsingOptions, format, formatFinishDate, isValid, parse } from "@cervval.js/common/src/tools/timestamps.tools";
import { DialogSize } from "../../../components/dialog/dialog";
import { CjsInput } from "../../../components/inputs/input";
import { CjsDateIntervalCalendarPicker, DateIntervalCalendarCalendarPickerSelection, DateIntervalCalendarPickerOptions } from "../../../pickers/dateIntervalCalendarPicker/dateIntervalCalendarPicker";
import { I18nType, i18n } from "../../../pickers/dateIntervalCalendarPicker/i18n/i18n";
import { DateIntervalPickerSelection } from "../../../pickers/dateIntervalPicker/dateIntervalPicker";
import { DateEditorOptions } from "../dateEditor/dateEditor";
import { DateInRangesValidator } from "../dateEditor/dateEditor.validators";
import { EditorWithPicker, defaultEditorWithPickerOptions } from "../editorWithPicker";

export type DateIntervalCalendarEditorOptions = DateEditorOptions & {
    enableFloatingLabels: boolean
};

export const defaultDateIntervalCalendarEditorOptions: DateIntervalCalendarEditorOptions = {
    ...defaultEditorWithPickerOptions,
    buttonIcon: { name: "fa-calendar-range" },
    enableFloatingLabels: false,
    popupOptions: {
        popupInDialogOptions: {
            popupCanBeCloseOnOuterBoundsClick: false,
            dialogOptions: { size: DialogSize.EXTRA_LARGE }
        }
    }
};

@CustomElement({
    name: "cjs-date-interval-calendar-editor",
    style: require("./dateIntervalCalendarEditor.scss"),
    i18n,
    elementCssClasses: ["cjs-editor"]
})
export class CjsDateIntervalCalendarEditor extends EditorWithPicker<DateIntervalCalendarCalendarPickerSelection, I18nType> {

    /** Attributes options of editor */
    @TypedWebComponentOptions(defaultDateIntervalCalendarEditorOptions)
    public readonly options!: WebComponentOptions<DateIntervalCalendarEditorOptions>;

    /** Shortcut to dateInterval picker options */
    public get pickerOptions(): WebComponentOptions<DateIntervalCalendarPickerOptions> {
        return this._picker.options;
    }

    /**
     * The DateIntervalCalendarPickerComponent used to edit the values of the input
     */
    protected override _picker = new CjsDateIntervalCalendarPicker();

    /** Start date interval input */
    public startDateInput = this._inputWithButton.input;

    /**
     * Finish date interval input 
     * ! Value of date is a cjs "FinishDate", but displayed value is a classic date !
     */
    public finishDateInput = new CjsInput<string>();

    constructor(options?: Partial<DateIntervalCalendarEditorOptions>, dateIntervalCalendarPickerOptions?: Partial<DateIntervalCalendarPickerOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
        this.pickerOptions.setWithoutRefresh(dateIntervalCalendarPickerOptions);
    }

    /** Init of calendar editor */
    public override init(): void {
        super.init();

        const inputsSeparator = createHtmlElement("div", {
            innerHTML: "-",
            cssClass: [FLEX_CONTAINER_CENTERED, FONT_WEIGHT.BOLD, MARGIN.MX_2]
        });

        // Init finish date input
        this.finishDateInput.classList.add(FLEX_CONTAINER, FLEX_ITEM_AUTO, MARGIN.ME_1, FLEX_WRAP.NO_WRAP);

        // Set finishDate input after input
        this._inputWithButton.input.after(inputsSeparator, this.finishDateInput);

        // Add same listeners as classic inputWithButtons
        this.finishDateInput.changeEvent.on((e) => {
            this._onInputValueChange(e);
        });

        this.finishDateInput.addEventListener("keyup", (e) => {
            if (e.key === KeyboardInput.ENTER) {
                this._onInputValueChange(e);
            }
        });
    }

    /** Refresh of calendar interval editor */
    public override refresh(): void {
        super.refresh();
        this._refreshInputs();
    }

    /**
     * Sets _value from input value and triggers onValueChange if value changed
     * Is triggered by editor when input value changes
     */
    protected override _onInputValueChange(event?: Event | anyReallyAny): void {
        const newValue = this._getValueFromInputsComponent();
        this._setValueIfDifferentAndDispatchEvents(newValue);
    }

    /** Refresh inputs */
    private _refreshInputs(): void {
        this.startDateInput.options.setAndRefreshIfChanged({
            floatingLabel: {
                enable: this.options.enableFloatingLabels,
                text: this.i18n.labels.startDate
            },
            placeholderText: this.i18n.labels.formatPlaceholder
        });
        this.finishDateInput.options.setAndRefreshIfChanged({
            floatingLabel: {
                enable: this.options.enableFloatingLabels,
                text: this.i18n.labels.finishDate
            },
            placeholderText: this.i18n.labels.formatPlaceholder
        });
    }

    /**
     * DateIntervalPicker getValue can't be used => override
     */
    protected override _getPickerValue(): DateIntervalCalendarCalendarPickerSelection | undefined {
        return this._picker.getValueInterval();
    }

    /**
     * Instantiate picker on first call, and refresh picker based on value and options (called before showPopup)
     */
    protected _refreshPicker(): CjsDateIntervalCalendarPicker {

        if (this._value != null) {
            // Set dateIntervalPicker value
            this._picker.setValue(clone(this._value));
            this._picker.refresh();
        } else {// Undefined, reset picker // TODO Create and replace with empty picker
            this._picker = new CjsDateIntervalCalendarPicker(this.pickerOptions);
        }
        return this._picker;
    }

    /** Not used for date interval calendar editor, replaced by "convertValueToInputsValue" */
    public convertValueToInputValue(interval: DateIntervalPickerSelection | undefined): string {
        return "";
    }

    /**
     * Format the value to a string which can be display for user
     */
    public convertValueToInputsValue(interval: DateIntervalPickerSelection | undefined, input: "start" | "finish"): string {
        const intervalValue = input === "start" ? interval?.start : interval?.finish;

        if (intervalValue) {
            if (interval?.start != null && interval.finish != null && !this._validateRanges(interval)) { return ""; }
            if (input === "start") {
                return format(intervalValue, DEFAULT_DATE_FORMAT);
            } else {
                return formatFinishDate(intervalValue, DEFAULT_DATE_FORMAT);
            }
        } else {
            return "";
        }
    }

    /**
     * Get start/end date formats from the string in input
     */
    protected _convertInputsValueToValue(inputValue: string | undefined, input: "start" | "finish"): DateIntervalPickerSelection | undefined {
        if (inputValue == null) {
            return undefined;
        }

        const parsedValue = parse(inputValue, DEFAULT_DATE_FORMAT, extractLooseParsingOptions(this.options)).uts;
        const startValue = input === "start" ? parsedValue : this._value?.start;
        const finishValue = input === "finish" ? beginOfNext(parsedValue, "day").uts : this._value?.finish;
        const isInRange = this._validateRanges({ start: startValue, finish: finishValue });

        if (isValid(parsedValue) && isInRange) {
            return { start: startValue, finish: finishValue };
        }

        return undefined;
    }


    /**
     * Gets the value corresponding to the current input value
     */
    protected _getValueFromInputComponent(): DateIntervalPickerSelection | undefined {
        return undefined;
    }

    /**
     * Gets the value corresponding to the current input value
     */
    protected _getValueFromInputsComponent(): DateIntervalPickerSelection | undefined {
        const start = this._convertInputsValueToValue(this.startDateInput.value, "start")?.start;
        const finish = this._convertInputsValueToValue(this.finishDateInput.value, "finish")?.finish;
        return { start, finish };
    }

    /**
     * Sets _value from input value and triggers onValueChange if value changed
     * Is triggered by editor when input value changes
     */
    // TODO refactor to use a specific comparator instead of overwriting function
    protected override _setValueIfDifferentAndDispatchEvents(newValue: DateIntervalPickerSelection | undefined): boolean {

        // Change editor's value only if picker's value is correct and different from current editor's value
        if (newValue?.start !== this._value?.start || newValue?.finish !== this._value?.finish ||
            newValue?.start == null) {
            this._value = clone(newValue);

            // reformat value in input if needed
            this._refreshInputValue();// TODO maybe editor dependent ?

            // dispatch events
            this._dispatchValueChangedEvent();
            return true;
        }
        return false;
    }

    /** Set formCheckInput as validation form element */
    public override getValidationFormElement(): HTMLElement | HTMLElement[] {
        return [this.getInput().input.input, this.finishDateInput.input];
    }

    /**
     * Update the value of the input from _value of the Editor
     */
    protected override _refreshInputValue(): void {
        const startValue = this.convertValueToInputsValue(this._value, "start");
        const finishValue = this.convertValueToInputsValue(this._value, "finish");

        this._inputWithButton.setValue(startValue);
        if (this._inputWithButton.isInitDone) {
            // if !isInitDone refresh will be called later
            this._inputWithButton.refresh();
        }

        this.finishDateInput.value = finishValue;
        this.finishDateInput.refresh();
    }

    // TODO refactor BGS I don't see the difference with the DateEditor function => use DateEditor function
    /** Validate value in enabled ranges of dateIntervalEditor */
    protected _validateRanges(value: DateIntervalPickerSelection): boolean {
        const rangesPropertyConfig: PropertyConfig = {
            propertyName: "ranges",
            label: "",
            typeUid: PropertyConfigType.DATE,
            validators: {
                ...validatorEntry(DateInRangesValidator, { ranges: this.pickerOptions.calendarsOptions?.ranges ?? [] })
            }
        };
        const propsValues: Record<string, unknown> = {
            "ranges": value ?? this._value
        };
        const validatedProps: Record<string, PropertyConfig> = {
            "ranges": rangesPropertyConfig
        };

        const errors = this._validation.validate(validatedProps, propsValues);

        this.setValidationError(errors["ranges"]);

        // Check if no error given === valid interval
        return Object.keys(errors).length === 0;
    }

    /** Update disabled attribute on input component */
    protected override _refreshDisabled(): void {
        super._refreshDisabled();
        this.finishDateInput.options.setAndRefreshIfChanged({ disabled: this.options.disabled });
    }

    /** Callback called after inputWithButton click */
    protected override _afterButtonClick(): void {
        this.finishDateInput.options.setAndRefreshIfChanged({ disabled: true });
    }

    /** Callback called after popup will hide and cancelled */
    protected override _afterPopupHideOrCancel(): void {
        this.finishDateInput.options.setAndRefreshIfChanged({ disabled: false });
    }
}